import {match} from 'ts-pattern';

import {KeyboardEvent, PointerEvent, useState} from 'react';

import {clamp, isNil} from 'ramda';
import {isUndefined} from 'ramda-adjunct';

import {EMPTY_PLACEHOLDER, Nullish} from 'shared/web';

import {LevelInputProps} from './LevelInputProps';
import {formatPercent} from './utils/formatPercent';
import {getLevelBandColor} from './utils/getLevelBandColor';
import {DEFAULT_STEP, PERCENT_SCALE, resolveLevel} from './utils/resolveLevel';
import {snapLevel} from './utils/snapLevel';

/** PageUp/PageDown move a quarter of the range, as agreed in the design review. */
const PAGE_STEP_DIVISOR = 4;

/**
 * Shared behaviour of the two level gauges — pointer scrubbing, keyboard
 * stepping, clearing to "not recorded", and the derived geometry both of them
 * paint. Returns the props to spread onto the interactive element, so the two
 * components differ only in how they draw the level.
 */
export function useLevelInput(props: LevelInputProps) {
  const {min, max, fraction, percent, isRecorded} = resolveLevel(props.value, props.min, props.max);
  const step = props.step ?? DEFAULT_STEP;

  /** Value the running gesture started from, restored when the gesture is cancelled. */
  const [dragOrigin, setDragOrigin] = useState<{value: number | Nullish} | null>(null);
  const [hoverFraction, setHoverFraction] = useState<number | null>(null);

  const isDragging = !isNil(dragOrigin);

  const commit = (value: number | null) => {
    props.onChange?.(snapLevel(value, min, max, step));
  };

  /** `null` while the track has no width. */
  const getPointerFraction = (event: PointerEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();

    if (rect.width === 0) {
      return null;
    }

    return clamp(0, 1, (event.clientX - rect.left) / rect.width);
  };

  const commitAtPointer = (event: PointerEvent<HTMLDivElement>) => {
    const pointerFraction = getPointerFraction(event);

    if (isNil(pointerFraction)) {
      return;
    }

    commit(min + pointerFraction * (max - min));
  };

  const releasePointer = (event: PointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  /**
   * Undoes the whole gesture: the level goes back to exactly what it was before
   * pointerdown. The origin is written straight to `onChange` rather than
   * through `snapLevel`, so a value that did not sit on the step grid survives
   * a cancel unchanged.
   */
  const restoreDragOrigin = () => {
    if (isNil(dragOrigin)) {
      return;
    }

    props.onChange?.(dragOrigin.value);
    setDragOrigin(null);
  };

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (props.isDisabled) {
      return;
    }

    /*
     * The hover preview sitting under the cursor unmounts the instant the drag
     * starts, so the browser resolves its default mousedown focus against a node
     * that has left the document, finds no focusable ancestor and clears focus to
     * <body> — after which Escape never reaches `onKeyDown`. Suppress that default
     * and take the focus explicitly, identically on both gauges.
     */
    event.preventDefault();
    event.currentTarget.focus({preventScroll: true});
    event.currentTarget.setPointerCapture(event.pointerId);
    setDragOrigin({value: props.value});
    setHoverFraction(null);
    commitAtPointer(event);
  };

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (props.isDisabled) {
      return;
    }

    if (isDragging) {
      commitAtPointer(event);
      return;
    }

    setHoverFraction(getPointerFraction(event));
  };

  const handlePointerUp = (event: PointerEvent<HTMLDivElement>) => {
    releasePointer(event);
    setDragOrigin(null);
  };

  /** An interrupted gesture counts as cancelled, exactly like Escape. */
  const handlePointerCancel = (event: PointerEvent<HTMLDivElement>) => {
    releasePointer(event);
    restoreDragOrigin();
  };

  const handlePointerLeave = () => setHoverFraction(null);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    props.onKeyDown?.(event);

    if (props.isDisabled) {
      return;
    }

    if (event.key === 'Escape' && isDragging) {
      event.preventDefault();
      restoreDragOrigin();
      return;
    }

    const current = props.value ?? min;
    const pageStep = (max - min) / PAGE_STEP_DIVISOR;

    const nextValue = match<string, number | null | undefined>(event.key)
      .with('ArrowLeft', 'ArrowDown', () => current - step)
      .with('ArrowRight', 'ArrowUp', () => current + step)
      .with('PageDown', () => current - pageStep)
      .with('PageUp', () => current + pageStep)
      .with('Home', () => min)
      .with('End', () => max)
      .with('Backspace', 'Delete', () => null)
      .otherwise(() => undefined);

    if (isUndefined(nextValue)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    commit(nextValue);
  };

  const trackProps = {
    role: 'slider',
    tabIndex: props.isDisabled ? -1 : 0,
    'aria-valuemin': 0,
    'aria-valuemax': PERCENT_SCALE,
    'aria-valuenow': percent,
    'aria-valuetext': isRecorded ? formatPercent(percent) : EMPTY_PLACEHOLDER,
    'aria-label': props.label ?? undefined,
    'aria-disabled': props.isDisabled ? true : undefined,
    'aria-invalid': props.isInvalid || undefined,
    'data-disabled': props.isDisabled ? '' : undefined,
    onPointerDown: handlePointerDown,
    onPointerMove: handlePointerMove,
    onPointerUp: handlePointerUp,
    onPointerCancel: handlePointerCancel,
    onPointerLeave: handlePointerLeave,
    onKeyDown: handleKeyDown,
    onFocus: props.onFocus,
    onBlur: props.onBlur,
  };

  return {
    fraction,
    percent,
    isRecorded,
    hoverFraction: isDragging ? null : hoverFraction,
    bandColor: getLevelBandColor(percent),
    valueText: isRecorded ? formatPercent(percent) : null,
    trackProps,
    setEmpty: () => commit(min),
    setFull: () => commit(max),
  };
}
